<template>
  <div>
    <span v-if="!page.subscribers.is_actual" class="warning-text-panel"> {{ $t('DASH_SUBSCRIBERS_WARNING') }}</span>
    <div class="one-page">
      <div class="col-6" v-if="page.posts.length">
        <div class="panel main-info">
          <h2>{{ $t('DASH_TOTAL') }}</h2>
          <div class="cont">
            <ul class="sums">
              <li
                @click="selectCell(name)"
                :class="isActiveCell(name)"
                :key="index"
                v-for="({ locale, value }, name, index) in page.stats.total"
              >
                <span :class="activeColor(name)">{{ formatNum(value) }}</span>
                {{ locale }}
              </li>
            </ul>

            <div class="m-chart">
              <div
                class="chart"
                ref="overviewOneStacked"
              >
               
              </div>
              <span class="chart-export"><i class="bi-download"></i>
                <ul class="export-list">
                  <li @click="exportStackedChart('svg')">SVG</li>
                  <li @click="exportStackedChart('csv')">CSV</li>
                </ul>
              </span>
              <div class="period-btns">
                <button @click="setPeriod('daily')" :class="{ active: activePeriod === 'daily' }">{{ $t('DASH_DAILY') }}</button>
                <button @click="setPeriod('weekly')" :class="{ active: activePeriod === 'weekly' }">{{ $t('DASH_WEEKLY') }}</button>
                <button @click="setPeriod('monthly')" :class="{ active: activePeriod === 'monthly' }">{{ $t('DASH_MONTHLY') }}</button>
              </div>
            </div>
          </div>
        </div>

        <div class="panel main-eng">
          <h2>{{ $t('DASH_ENG') }}</h2>
          <ul class="sums">
            <li
              :key="index"
              v-for="({ locale, value }, name, index) in page.stats.engagement"
            >
              <span>{{ formatNum(value) }}</span>
              {{ locale }}
              <div class="info-panel" :class="{ 'active': activeInfoPanel === index }">
                <i class="bi bi-info-square" @click.stop="toggleInfoPanel($event, index)"></i>
                <div class="info-panel-content" :ref="`infoPanelContent_${index}`">
                  {{ getMetricDescription(name) }}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div class="col-4">
        <div class="panel main-av" v-if="Object.keys(page.stats.average).length">
          <h2>{{ $t('DASH_AVE') }}</h2>
          <ul class="sums">
            <li
              :key="index"
              v-for="({ locale, value }, name, index) in page.stats.average"
            >
              <span>{{ formatNum(value) }}</span>
              {{ locale }}
            </li>
          </ul>
        </div>
        <div class="panel main-foll">
          <h2>{{ $t('STATS_TABS_COMMON_SUBSCRIBERS') }}</h2>
          <ul class="sums">
            <li>
              <span>{{
                formatNum(page.stats.followers.subscribers.value)
              }}</span>
              {{ $t('STATS_TABLE_FOLLOWERS_SUBSCRIBERS') }}
            </li>
            <li>
              <span :class="classGrowth">{{
                formatNum(page.stats.followers.growth.value)
              }}</span>
              {{ $t('INTRO_TABS_GROWTH') }}
            </li>
          </ul>
          <div class="m-chart">
            <div
              class="chart"
              ref="overviewOneLines"
            ></div>
            <span class="chart-export"><i class="bi-download"></i>
              <ul class="export-list">
                <li @click="exportLinesChart('svg')">SVG</li>
                <li @click="exportLinesChart('csv')">CSV</li>
              </ul>
            </span>
            <div class="period-btns">
              <button @click="setPeriod('daily')" :class="{ active: activePeriod === 'daily' }">{{ $t('DASH_DAILY') }}</button>
              <button @click="setPeriod('weekly')" :class="{ active: activePeriod === 'weekly' }">{{ $t('DASH_WEEKLY') }}</button>
              <button @click="setPeriod('monthly')" :class="{ active: activePeriod === 'monthly' }">{{ $t('DASH_MONTHLY') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import { ref } from "vue"
import pageUtils from "@/models/page-utils";
import stackedOnePageChart, { exportChart as exportStackedChart } from "@/models/charts/estacked-overview";
import linesOnePageChart, { exportChart as exportLinesChart } from "@/models/charts/elines";
import utils from "@/utils";

export default {
  name: "Dash-overview-one",
  components: {},
  computed: {
    ...mapGetters(["getAnalizedPages", "getUnionPagesData"]),

    page() {
      return this.getAnalizedPages[0];
    },
    classGrowth() {
      const intGrowth = parseInt(this.page.stats.followers.growth.value);
      if (intGrowth > 0) return "positive";
      if (intGrowth < 0) return "negative";
      return "";
    },
  },
  data() {
    return {
      activeCells: [],
      availableColors: Object.entries(this.$colors).map(
        ([key, value], index) => {
          return { key, value, index };
        }
      ),
      activeColors: [],
      loaded: false,
      charts: {
        total: undefined,
        subscribers: undefined,
      },
      activeInfoPanel: null,
      activePeriod: 'daily', // default to daily view
    };
  },
  methods: {
    formatNum(num) {
      return pageUtils.formatNumbers(num);
    },
    isActiveCell(cell) {
      if (this.activeCells.includes(cell)) return "active";
      return "";
    },
    async selectCell(cell) {
      const index = this.activeCells.indexOf(cell);
      if (index == -1) {
        this.activeCells.push(cell);
        this.activeColors.push(this.availableColors.shift());
      } else {
        this.activeCells.splice(index, 1);
        this.availableColors.push(this.activeColors[index]);
        this.activeColors.splice(index, 1);

        this.availableColors = this.availableColors.sort(
          (a, b) => a.index - b.index
        );
      }

      const { pages, datesRangePosts, datesRangeStories } = this.$store.getters[
        "getUnionPagesData"
      ];
      await this.renderTotalChart(pages, datesRangePosts, datesRangeStories);
    },
    activeColor(cell) {
      const index = this.activeCells.indexOf(cell);
      if (index == -1) return "";
      return this.activeColors[index].key;
    },
    async renderSubscribersChart() {
      const { pages, datesRangePosts, datesRangeStories } = this.$store.getters[
        "getUnionPagesData"
      ];
      if (this.charts.subscribers) this.charts.subscribers.dispose();

      const chartElement = this.getRefElement("overviewOneLines");
      if (!chartElement) {
        return;
      }

      this.charts.subscribers = await utils.initializeChart({
        chartFn: linesOnePageChart,
        data: {
          pages,
          datesRangePosts,
          name: "subscribers",
          type: "overview-one"
        },
        container: chartElement,
        config: {
          color: ref(window.colors.green),
          period: this.activePeriod
        }
      });
    },
    async renderTotalChart(pages, datesRangePosts, datesRangeStories) {
      if (this.charts.total) this.charts.total.dispose();
      if (!this.page.posts.length) return;

      const chartElement = this.getRefElement("overviewOneStacked");
      if (!chartElement) {
        return;
      }

      this.charts.total = await utils.initializeChart({
        chartFn: stackedOnePageChart,
        data: {
          pages,
          datesRangePosts,
          datesRangeStories,
          names: this.activeCells
        },
        container: chartElement,
        config: {
          colors: this.activeColors,
          period: this.activePeriod
        }
      });
    },
    async renderCharts() {
      const { pages, datesRangePosts, datesRangeStories } = this.$store.getters[
        "getUnionPagesData"
      ];
      await Promise.all([
        this.renderTotalChart(pages, datesRangePosts, datesRangeStories),
        this.renderSubscribersChart()
      ]);
    },
    toggleInfoPanel(event, index) {
      if (this.activeInfoPanel !== null && this.activeInfoPanel !== index) {
        this.activeInfoPanel = null;
      }
      if (this.activeInfoPanel === index) {
        this.activeInfoPanel = null;
      } else {
        this.activeInfoPanel = index;
      }
      
      event.stopPropagation();
    },
    handleClickOutside(event) {
      if (this.activeInfoPanel !== null) {
        if (!event.target.closest('.info-panel-content') && 
            !event.target.classList.contains('bi-info-square')) {
          this.activeInfoPanel = null;
        }
      }
    },
    getMetricDescription(metricName) {
      const translationKey = `METRIC_DESC_${metricName.toUpperCase()}`;
      if (this.$te(translationKey)) {
        return this.$t(translationKey);
      } else {
        return this.$t('DASH_NO_DESC_AVAILABLE', 'No description available for this metric.');
      }
    },
    setPeriod(period) {
      if (this.activePeriod !== period) {
        this.activePeriod = period;
        const { pages, datesRangePosts, datesRangeStories } = this.$store.getters[
          "getUnionPagesData"
        ];
        this.renderTotalChart(pages, datesRangePosts, datesRangeStories);
        this.renderSubscribersChart();
      }
    },
    async exportStackedChart(type) {
      if (!this.charts.total) {
        return;
      }
      
      const chartContainer = this.getRefElement("overviewOneStacked");
      if (!chartContainer) {
        return;
      }
      
      await exportStackedChart({
        chart: this.charts.total,
        container: chartContainer,
        chartData: this.$store.getters["getUnionPagesData"],
        activeCells: this.activeCells,
        activeColors: this.activeColors,
        period: this.activePeriod,
        type
      });
    },
    async exportLinesChart(type) {
      if (!this.charts.subscribers) {
        return;
      }
      
      const chartContainer = this.getRefElement("overviewOneLines");
      if (!chartContainer) {
        return;
      }
      
      await exportLinesChart({
        chart: this.charts.subscribers,
        container: chartContainer,
        chartData: this.$store.getters["getUnionPagesData"],
        name: "subscribers",
        type: "overview-one",
        color: ref(window.colors.green),
        period: this.activePeriod,
        exportType: type
      });
    },
    
    getRefElement(refName) {
      const ref = this.$refs[refName];
      if (!ref) {
        return null;
      }
      return Array.isArray(ref) ? ref[0] : ref;
    },
  },
  mounted() {
    setTimeout(() => {
      this.selectCell("likes");
      this.renderSubscribersChart();
    });
    
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>
